import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, MapPin, User, Car, CreditCard, Truck, Calendar, FileText, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  getVehicleMakes, 
  getVehicleModels, 
  getVehicleYears, 
  getVehicleColors,
  getServices,
  createBooking,
  createCheckoutSession
} from '../lib/api';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { MobileBookingActionBar } from '../components/MobileBookingActionBar';

const LOGO_URL = "/pt-logo.png?v=2";

const STEPS = [
  { id: 1, name: 'Service', icon: Truck },
  { id: 2, name: 'Info', icon: User },
  { id: 3, name: 'Vehicle', icon: Car },
  { id: 4, name: 'Pickup', icon: Calendar },
  { id: 5, name: 'Addresses', icon: MapPin },
  { id: 6, name: 'Terms', icon: FileText },
  { id: 7, name: 'Payment', icon: CreditCard },
];

// Fixed pickup schedules
const PICKUP_SCHEDULE = {
  lakewood_to_orlando: {
    date: "Thursday, March 26th, 2026",
    time: "12:00 PM - 5:00 PM",
    location: "Lakewood, NJ"
  },
  orlando_to_lakewood: {
    date: "Thursday, April 9th, 2026", 
    time: "12:00 PM - 5:00 PM",
    location: "Orlando, FL"
  }
};

const TERMS_AND_CONDITIONS = `PLATINUM TOUCH NJ LLC
PHONE: 682-253-2633
EMAIL: PlatinumTouchNJ@gmail.com

VEHICLE TRANSPORT SERVICE AGREEMENT
Terms & Conditions

1. SHUTTLE AUTHORIZATION
The Customer authorizes Company personnel to drive the vehicle for the "shuttle" portions of this trip. This includes driving the vehicle:
   a. From the Lakewood home to the transport trailer (and vice versa).
   b. From the Orlando trailer drop-point to the final home (and vice versa).

2. INSURANCE COVERAGE
The Customer acknowledges that the Motor Truck Cargo Insurance ($250,000 limit) is ONLY in effect while the vehicle is physically loaded on the transport trailer.

No coverage is provided by Platinum Touch LLC while the vehicle is being driven on its own wheels "on the way out" to the truck or "on the way in" to the residence. During these ground-shuttle intervals, the Customer's personal auto insurance remains the primary coverage.

3. PERSONAL BELONGINGS
**The front seating area, including both front seats and all front floor space, must be completely free of personal belongings at the time of pickup. The Carrier and Company assume no responsibility for loss of or damage to any personal items left anywhere inside the vehicle.**

4. TOLL TRANSPONDERS
**Customer must remove, deactivate, or properly shield any and all toll transponders, including but not limited to E-ZPass, SunPass, or similar devices. The Company is not responsible for any tolls, fees, violations, or related charges incurred at any time during transport, including while the vehicle is loaded on the trailer.**

5. DAMAGE CLAIMS & BILL OF LADING
Any alleged damage must be noted in writing on the Bill of Lading (BOL) at the time of delivery and before the driver leaves the delivery location. Once the vehicle is accepted, the keys are returned, and the Company representative departs, the shipment is deemed complete and free of new damage, and all claims are permanently waived.

6. CANCELLATION, DELAYS & FORCE MAJEURE
The Company reserves the right to cancel, reschedule, or delay service at its sole discretion due to weather conditions, traffic, mechanical issues, driver availability, road closures, government restrictions, or any force majeure event beyond its control. Such events shall not constitute a breach of this Agreement, and the Company shall not be liable for any resulting losses, expenses, or consequential damages.

Cancellation Policy:
- Cancellations made 14+ days before scheduled pickup: Full refund minus $100 processing fee
- Cancellations made 7-13 days before scheduled pickup: 50% refund
- Cancellations made less than 7 days before scheduled pickup: No refund

7. VEHICLE SECURITY SYSTEMS & KEYS
Customer is solely responsible for disabling all alarms, anti-theft systems, kill switches, tracking devices, and similar equipment prior to pickup. The Company shall not be responsible for battery drain, alarm activation, immobilization, or related issues arising from such systems. Customer must provide at least one fully functional key or key fob at the time of pickup. The Company is not liable for lost, misplaced, or damaged keys except in cases of proven gross negligence.

8. VEHICLE CONDITION & EXCLUSIONS
Customer acknowledges that vehicles may arrive dirty due to weather, road conditions, or normal transit. Minor scratches, paint chips, small dents, curb rash, and ordinary wear and tear are considered pre-existing or incidental and are not compensable. The Company assumes no liability for mechanical or electrical failures; suspension damage; alignment issues; undercarriage damage; fluid leaks; overheating; warning lights; aftermarket or modified components; low-clearance damage; or any damage resulting from acts of God, road debris, or normal transportation risks.

9. LUGGAGE POLICY
**If Customer elects to ship luggage or personal belongings, such items must be declared in advance and approved by the Company. ALL LUGGAGE MUST BE PLACED EXCLUSIVELY INTO THE STORAGE CONTAINER TOTES PROVIDED BY THE COMPANY; LOOSE BAGS OR ITEMS WILL NOT BE ACCEPTED. Luggage will be transferred from the vehicle to a separate transport truck that carries designated cargo coverage and will be reloaded into the vehicle upon arrival. Customer acknowledges that luggage transport is separate from vehicle transport and subject to the terms, conditions, exclusions, and limitations of the applicable cargo policy. The Company is not responsible for undeclared items, prohibited contents, fragile items, cash, jewelry, electronics, collectibles, or other valuables.

HAZARDOUS, FLAMMABLE, EXPLOSIVE, ILLEGAL, OR OTHERWISE DANGEROUS MATERIALS ARE STRICTLY PROHIBITED.

In all cases, the Company's maximum total liability for luggage shall not exceed $5,000 per person, regardless of the number of items, totes, or claims.**

10. PAYMENT TERMS
- Full payment is required at the time of booking
- All prices are subject to NJ sales tax (6.625%)
- Refunds are subject to our cancellation policy

11. GOVERNING LAW & DISPUTES
Drivers and carriers utilized by the Company may be independent contractors, and nothing in this Agreement shall be construed to create any employment, agency, joint venture, or partnership relationship. This Agreement shall be governed by and construed in accordance with the laws of the State of New Jersey. Any dispute arising out of or relating to this Agreement shall be brought exclusively in a court of competent jurisdiction located in New Jersey. If any provision of this Agreement is held invalid or unenforceable, the remaining provisions shall remain in full force and effect. This document constitutes the entire agreement between the parties and supersedes all prior or contemporaneous verbal or written communications. Customer's printed name, contact information, signature, and date constitute acknowledgment and acceptance of all terms and conditions herein.

ACKNOWLEDGMENT & AGREEMENT:
I the undersigned hereby acknowledge that I have read, understand, and agree to all terms and conditions set forth in this Vehicle Transport Service Agreement, including all limitations of liability, exclusions, and responsibilities described herein. I confirm that the information provided is accurate and that I am authorized to release this vehicle and any declared luggage for transport.`;

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [justCompletedStep, setJustCompletedStep] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Data states
  const [services, setServices] = useState([]);
  const [vehicleMakes, setVehicleMakes] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [vehicleYears, setVehicleYears] = useState([]);
  const [vehicleColors, setVehicleColors] = useState([]);
  
  // Zelle payment states
  const [showZellePending, setShowZellePending] = useState(false);
  const [zelleBookingData, setZelleBookingData] = useState(null);
  
  // Form states
  const [selectedService, setSelectedService] = useState(searchParams.get('service') || '');
  const [customerInfo, setCustomerInfo] = useState({
    full_name: '',
    phone: '',
    email: ''
  });
  const [vehicleInfo, setVehicleInfo] = useState({
    year: '',
    make: '',
    model: '',
    color: ''
  });
  const [lakewoodAddress, setLakewoodAddress] = useState({
    street: '',
    city: 'Lakewood',
    state: 'NJ',
    zip_code: '08701'
  });
  const [orlandoAddress, setOrlandoAddress] = useState({
    street: '',
    city: 'Orlando',
    state: 'FL',
    zip_code: '32801'
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [servicesRes, makesRes, yearsRes, colorsRes] = await Promise.all([
          getServices(),
          getVehicleMakes(),
          getVehicleYears(),
          getVehicleColors()
        ]);
        setServices(servicesRes.data.services);
        setVehicleMakes(makesRes.data.makes);
        setVehicleYears(yearsRes.data.years);
        setVehicleColors(colorsRes.data.colors);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load form data');
      }
    };
    loadData();
  }, []);

  // Load models when make changes
  useEffect(() => {
    const loadModels = async () => {
      if (vehicleInfo.make) {
        try {
          const response = await getVehicleModels(vehicleInfo.make);
          setVehicleModels(response.data.models);
          setVehicleInfo(prev => ({ ...prev, model: '' }));
        } catch (error) {
          console.error('Error loading models:', error);
        }
      } else {
        setVehicleModels([]);
      }
    };
    loadModels();
  }, [vehicleInfo.make]);

  const selectedServiceData = services.find(s => s.id === selectedService);
  const isRoundTrip = selectedService === 'round_trip';
  const isToOrlando = selectedService === 'lakewood_to_orlando' || isRoundTrip;
  const isToLakewood = selectedService === 'orlando_to_lakewood' || isRoundTrip;

  // US Phone number validation
  const isValidUSPhone = (phone) => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    // US phone numbers should have 10 digits (or 11 if starting with 1)
    if (digits.length === 10) return true;
    if (digits.length === 11 && digits.startsWith('1')) return true;
    return false;
  };

  // Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!selectedService) {
          toast.error('Please select a service');
          return false;
        }
        return true;
      case 2:
        if (!customerInfo.full_name || !customerInfo.phone || !customerInfo.email) {
          toast.error('Please fill in all customer information');
          return false;
        }
        if (!isValidUSPhone(customerInfo.phone)) {
          toast.error('Please enter a valid US phone number (10 digits)');
          return false;
        }
        if (!isValidEmail(customerInfo.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      case 3:
        if (!vehicleInfo.year || !vehicleInfo.make || !vehicleInfo.model || !vehicleInfo.color) {
          toast.error('Please fill in all vehicle information');
          return false;
        }
        return true;
      case 4:
        // Pickup schedule is fixed, no validation needed
        return true;
      case 5:
        if (!lakewoodAddress.street || !orlandoAddress.street) {
          toast.error('Please fill in the street addresses');
          return false;
        }
        return true;
      case 6:
        if (!acceptedTerms) {
          toast.error('Please accept the terms and conditions to continue');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setJustCompletedStep(currentStep);
      setCurrentStep(prev => Math.min(prev + 1, 7));
      setTimeout(() => setJustCompletedStep(null), 800);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmitBooking = async (paymentMethod = 'stripe') => {
    setIsLoading(true);
    try {
      // Build travel dates based on service type using fixed schedule
      const fixedTravelDates = {
        departure_to_orlando: isToOrlando ? PICKUP_SCHEDULE.lakewood_to_orlando.date : '',
        arrival_in_orlando: isToOrlando ? 'Within 2-3 days of pickup' : '',
        departure_to_lakewood: isToLakewood ? PICKUP_SCHEDULE.orlando_to_lakewood.date : '',
        arrival_in_lakewood: isToLakewood ? 'Within 2-3 days of pickup' : ''
      };

      const bookingData = {
        service_type: selectedService,
        customer: customerInfo,
        vehicle: vehicleInfo,
        travel_dates: fixedTravelDates,
        lakewood_address: lakewoodAddress,
        orlando_address: orlandoAddress,
        terms_accepted: acceptedTerms,
        payment_method: paymentMethod
      };
      
      const bookingResponse = await createBooking(bookingData);
      const bookingId = bookingResponse.data.id;
      const zelleDiscount = bookingResponse.data.zelle_discount || 0;
      
      if (paymentMethod === 'zelle') {
        // Show Zelle pending payment screen
        setZelleBookingData({
          bookingId,
          serviceName: selectedServiceData?.name,
          basePrice: selectedServiceData?.price || 0,
          discount: zelleDiscount,
          total: (selectedServiceData?.price || 0) - zelleDiscount,
          customerName: customerInfo.full_name
        });
        setShowZellePending(true);
        setIsLoading(false);
      } else {
        // Proceed with Stripe checkout
        const originUrl = window.location.origin;
        const checkoutResponse = await createCheckoutSession(bookingId, originUrl);
        
        if (checkoutResponse.data.url) {
          window.location.href = checkoutResponse.data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link-booking">
              <img src={LOGO_URL} alt="Platinum Touch" className="h-14 w-auto" />
              <span className="font-bold text-xl text-slate-900 font-['Outfit']">Platinum Touch</span>
            </Link>
            <Link 
              to="/"
              className="flex items-center gap-2 text-slate-600 hover:text-[#38B6FF] transition-colors"
              data-testid="back-home-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                      currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : currentStep === step.id
                        ? 'bg-[#38B6FF] text-white'
                        : 'bg-slate-100 text-slate-400'
                    } ${justCompletedStep === step.id ? 'animate-step-complete' : ''}`}
                    data-testid={`step-indicator-${step.id}`}
                  >
                    {currentStep > step.id ? (
                      <span className={justCompletedStep === step.id ? 'animate-check-draw' : ''}>
                        <Check className="w-5 h-5" />
                      </span>
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block transition-colors duration-300 ${
                    currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'
                  }`}>
                    {step.name}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 sm:mx-2 transition-colors duration-300 ${
                    currentStep > step.id
                      ? justCompletedStep === step.id ? 'bg-slate-200 animate-line-fill' : 'bg-green-500'
                      : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className={`max-w-3xl mx-auto px-4 py-10 ${currentStep < 7 ? 'pb-36 md:pb-10' : ''}`}>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10">
          
          {/* Step 1: Service Selection */}
          {currentStep === 1 && (
            <div className="animate-fade-in" data-testid="step-1-service">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Select Your Service</h2>
              <p className="text-slate-600 mb-8">Choose the transport option that fits your needs</p>
              
              <div className="space-y-4">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => setSelectedService(service.id)}
                    className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedService === service.id
                        ? 'border-[#38B6FF] bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`service-option-${service.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedService === service.id
                            ? 'border-[#38B6FF]'
                            : 'border-slate-300'
                        }`}>
                          {selectedService === service.id && (
                            <div className="w-2.5 h-2.5 rounded-full bg-[#38B6FF]" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{service.name}</h3>
                          <p className="text-sm text-slate-600">{service.description}</p>
                        </div>
                      </div>
                      <span className="text-xl font-bold text-slate-900 font-['Outfit']">
                        ${service.price.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Customer Information */}
          {currentStep === 2 && (
            <div className="animate-fade-in" data-testid="step-2-info">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Your Information</h2>
              <p className="text-slate-600 mb-8">Please provide your contact details</p>
              
              <div className="space-y-6">
                <div>
                  <Label htmlFor="full_name" className="text-slate-700 font-medium">Full Name</Label>
                  <Input
                    id="full_name"
                    value={customerInfo.full_name}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Enter your full name"
                    className="mt-2 h-12"
                    data-testid="input-full-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-slate-700 font-medium">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(123) 456-7890"
                    className="mt-2 h-12"
                    data-testid="input-phone"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="mt-2 h-12"
                    data-testid="input-email"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Vehicle Information */}
          {currentStep === 3 && (
            <div className="animate-fade-in" data-testid="step-3-vehicle">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Vehicle Information</h2>
              <p className="text-slate-600 mb-8">Tell us about the vehicle being transported</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-700 font-medium">Vehicle Year</Label>
                  <Select
                    value={vehicleInfo.year}
                    onValueChange={(value) => setVehicleInfo(prev => ({ ...prev, year: value }))}
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="select-year">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleYears.map((year) => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-700 font-medium">Vehicle Make</Label>
                  <Select
                    value={vehicleInfo.make}
                    onValueChange={(value) => setVehicleInfo(prev => ({ ...prev, make: value }))}
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="select-make">
                      <SelectValue placeholder="Select make" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleMakes.map((make) => (
                        <SelectItem key={make} value={make}>{make}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-700 font-medium">Vehicle Model</Label>
                  <Select
                    value={vehicleInfo.model}
                    onValueChange={(value) => setVehicleInfo(prev => ({ ...prev, model: value }))}
                    disabled={!vehicleInfo.make}
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="select-model">
                      <SelectValue placeholder={vehicleInfo.make ? "Select model" : "Select make first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleModels.map((model) => (
                        <SelectItem key={model} value={model}>{model}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-700 font-medium">Vehicle Color</Label>
                  <Select
                    value={vehicleInfo.color}
                    onValueChange={(value) => setVehicleInfo(prev => ({ ...prev, color: value }))}
                  >
                    <SelectTrigger className="mt-2 h-12" data-testid="select-color">
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicleColors.map((color) => (
                        <SelectItem key={color} value={color}>{color}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Pickup Schedule */}
          {currentStep === 4 && (
            <div className="animate-fade-in" data-testid="step-4-dates">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Your Pickup Schedule</h2>
              <p className="text-slate-600 mb-8">Here are your scheduled pickup times</p>
              
              <div className="space-y-6">
                {/* To Orlando Pickup */}
                {isToOrlando && (
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-[#38B6FF] rounded-full flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Lakewood, NJ → Orlando, FL</h3>
                        <p className="text-sm text-slate-500">Pickup from Lakewood</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <p className="text-lg font-semibold text-slate-900">
                        Your pickup will be on <span className="text-[#38B6FF]">{PICKUP_SCHEDULE.lakewood_to_orlando.date}</span>
                      </p>
                      <p className="text-slate-600 mt-1">
                        <span className="font-medium">{PICKUP_SCHEDULE.lakewood_to_orlando.time}</span> pickup window
                      </p>
                    </div>
                  </div>
                )}

                {/* To Lakewood Pickup */}
                {isToLakewood && (
                  <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Orlando, FL → Lakewood, NJ</h3>
                        <p className="text-sm text-slate-500">Pickup from Orlando</p>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <p className="text-lg font-semibold text-slate-900">
                        Your pickup will be on <span className="text-green-600">{PICKUP_SCHEDULE.orlando_to_lakewood.date}</span>
                      </p>
                      <p className="text-slate-600 mt-1">
                        <span className="font-medium">{PICKUP_SCHEDULE.orlando_to_lakewood.time}</span> pickup window
                      </p>
                    </div>
                  </div>
                )}

                <p className="text-sm text-slate-500 text-center">
                  We will contact you to confirm the exact pickup time within this window.
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Addresses */}
          {currentStep === 5 && (
            <div className="animate-fade-in" data-testid="step-5-addresses">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Pickup & Delivery Addresses</h2>
              <p className="text-slate-600 mb-8">Enter the street addresses for pickup and delivery</p>
              
              <div className="space-y-6">
                {/* Lakewood Address */}
                <div className="bg-slate-50 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#38B6FF]" />
                    Lakewood, NJ Address
                  </h3>
                  <div>
                    <Label className="text-slate-700 font-medium">Street Address</Label>
                    <AddressAutocomplete
                      className="mt-2"
                      placeholder="Start typing Lakewood address..."
                      onAddressSelect={(address) => {
                        setLakewoodAddress(prev => ({ 
                          ...prev, 
                          street: address.street || address.formatted_address || ''
                        }));
                      }}
                    />
                    <p className="text-sm text-slate-500 mt-2">Lakewood, NJ 08701</p>
                  </div>
                </div>

                {/* Orlando Address */}
                <div className="bg-slate-50 rounded-xl p-5">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#38B6FF]" />
                    Orlando, FL Address
                  </h3>
                  <div>
                    <Label className="text-slate-700 font-medium">Street Address</Label>
                    <AddressAutocomplete
                      className="mt-2"
                      placeholder="Start typing Orlando address..."
                      onAddressSelect={(address) => {
                        setOrlandoAddress(prev => ({ 
                          ...prev, 
                          street: address.street || address.formatted_address || ''
                        }));
                      }}
                    />
                    <p className="text-sm text-slate-500 mt-2">Orlando, FL 32801</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Terms & Conditions */}
          {currentStep === 6 && (
            <div className="animate-fade-in" data-testid="step-6-terms">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Terms & Conditions</h2>
              <p className="text-slate-600 mb-6">Please read and accept our terms before proceeding</p>
              
              <div className="bg-slate-50 rounded-xl p-4 mb-6 max-h-80 overflow-y-auto border border-slate-200">
                <div className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {TERMS_AND_CONDITIONS.split('\n').map((line, index) => {
                    // Check if line contains bold markers
                    if (line.startsWith('**') && line.endsWith('**')) {
                      return <p key={`term-${index}`} className="font-bold">{line.replace(/\*\*/g, '')}</p>;
                    } else if (line.includes('**')) {
                      // Handle lines that start with ** but continue
                      const parts = line.split('**');
                      return (
                        <p key={`term-${index}`}>
                          {parts.map((part, i) => 
                            i % 2 === 1 ? <strong key={`part-${index}-${i}`}>{part}</strong> : <span key={`part-${index}-${i}`}>{part}</span>
                          )}
                        </p>
                      );
                    }
                    return <p key={`term-${index}`}>{line}</p>;
                  })}
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => setAcceptedTerms(checked)}
                  className="mt-1"
                  data-testid="checkbox-terms"
                />
                <Label htmlFor="terms" className="text-slate-700 cursor-pointer leading-relaxed">
                  I have read and agree to the Terms and Conditions. I understand the cancellation policy, liability limitations, and my responsibilities as a customer.
                </Label>
              </div>
            </div>
          )}

          {/* Step 7: Review & Payment */}
          {currentStep === 7 && !showZellePending && (
            <div className="animate-fade-in" data-testid="step-7-payment">
              <h2 className="text-2xl font-bold text-slate-900 mb-2 font-['Outfit']">Review & Pay</h2>
              <p className="text-slate-600 mb-8">Please review your booking details before payment</p>
              
              <div className="space-y-6">
                {/* Pricing Breakdown - NO TAX for Auto Transport */}
                {selectedServiceData && (() => {
                  const basePrice = selectedServiceData.price;
                  const isRoundTrip = selectedService?.includes('round_trip');
                  const zelleDiscount = isRoundTrip ? 70 : 35;
                  const zelleTotal = basePrice - zelleDiscount;
                  
                  return (
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                      <h4 className="font-semibold text-slate-900 mb-4">Payment Summary</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">{selectedServiceData.name}</span>
                          <span className="text-slate-900 font-medium">${basePrice.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Sales Tax</span>
                          <span className="text-slate-900 font-medium">$0.00</span>
                        </div>
                        <div className="border-t border-slate-300 pt-3 mt-3">
                          <div className="flex justify-between">
                            <span className="text-lg font-semibold text-slate-900">Total</span>
                            <span className="text-2xl font-bold text-[#38B6FF] font-['Outfit']">${basePrice.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Zelle Savings Callout - Clickable */}
                      <div 
                        onClick={() => handleSubmitBooking('zelle')}
                        className="mt-4 bg-purple-50 border-2 border-purple-300 rounded-lg p-4 cursor-pointer hover:bg-purple-100 hover:border-purple-400 transition-all"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <img src="/zelle-logo.png" alt="Zelle" className="h-6 w-auto" />
                          <span className="font-semibold text-purple-900">Save with Zelle!</span>
                        </div>
                        <p className="text-sm text-purple-700">
                          Pay with Zelle and save <span className="font-bold">${zelleDiscount}</span> on your {isRoundTrip ? 'round trip' : 'one-way trip'}!
                        </p>
                        <p className="text-lg font-bold text-purple-600 mt-1">Your Zelle Price: ${zelleTotal.toFixed(2)}</p>
                        <p className="text-xs text-purple-500 mt-2">Click here to pay with Zelle →</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Customer Info Summary */}
                <div className="border border-slate-200 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 mb-3">Customer Information</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Name:</span>
                      <p className="text-slate-900 font-medium">{customerInfo.full_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Phone:</span>
                      <p className="text-slate-900 font-medium">{customerInfo.phone}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-slate-500">Email:</span>
                      <p className="text-slate-900 font-medium">{customerInfo.email}</p>
                    </div>
                  </div>
                </div>

                {/* Vehicle Summary */}
                <div className="border border-slate-200 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 mb-3">Vehicle Details</h4>
                  <p className="text-slate-900 font-medium">
                    {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
                  </p>
                  <p className="text-slate-600 text-sm">Color: {vehicleInfo.color}</p>
                </div>

                {/* Pickup Schedule Summary */}
                <div className="border border-slate-200 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 mb-3">Pickup Schedule</h4>
                  <div className="space-y-3 text-sm">
                    {isToOrlando && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <span className="text-slate-500">Lakewood → Orlando:</span>
                        <p className="text-slate-900 font-medium">
                          {PICKUP_SCHEDULE.lakewood_to_orlando.date}
                        </p>
                        <p className="text-slate-600 text-xs">{PICKUP_SCHEDULE.lakewood_to_orlando.time} pickup window</p>
                      </div>
                    )}
                    {isToLakewood && (
                      <div className="bg-green-50 rounded-lg p-3">
                        <span className="text-slate-500">Orlando → Lakewood:</span>
                        <p className="text-slate-900 font-medium">
                          {PICKUP_SCHEDULE.orlando_to_lakewood.date}
                        </p>
                        <p className="text-slate-600 text-xs">{PICKUP_SCHEDULE.orlando_to_lakewood.time} pickup window</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Addresses Summary */}
                <div className="border border-slate-200 rounded-xl p-5">
                  <h4 className="font-semibold text-slate-900 mb-3">Addresses</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Lakewood, NJ:</span>
                      <p className="text-slate-900 font-medium">
                        {lakewoodAddress.street}<br />
                        {lakewoodAddress.city}, {lakewoodAddress.state} {lakewoodAddress.zip_code}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500">Orlando, FL:</span>
                      <p className="text-slate-900 font-medium">
                        {orlandoAddress.street}<br />
                        {orlandoAddress.city}, {orlandoAddress.state} {orlandoAddress.zip_code}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-200">
            {currentStep > 1 ? (
              <Button
                variant="outline"
                onClick={handleBack}
                className="px-6"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}
            
            {currentStep < 7 ? (
              <Button
                onClick={handleNext}
                className="hidden bg-[#38B6FF] px-8 text-white hover:bg-blue-500 md:inline-flex"
                data-testid="next-btn"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <div className="flex flex-col gap-3 w-full md:w-auto md:flex-row">
                {/* Stripe Pay Button */}
                <Button
                  onClick={() => handleSubmitBooking('stripe')}
                  disabled={isLoading}
                  className="bg-[#38B6FF] hover:bg-blue-500 text-white px-8"
                  data-testid="pay-now-btn"
                >
                  {isLoading ? 'Processing...' : `Pay $${selectedServiceData ? selectedServiceData.price.toFixed(2) : '0.00'}`}
                  <CreditCard className="w-4 h-4 ml-2" />
                </Button>
                
                {/* Zelle Pay Button */}
                <Button
                  onClick={() => handleSubmitBooking('zelle')}
                  disabled={isLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 border-2 border-purple-400"
                  data-testid="pay-zelle-btn"
                >
                  <img src="/zelle-logo.png" alt="Zelle" className="h-5 w-auto mr-2 brightness-0 invert" />
                  Pay with Zelle - Save ${selectedService?.includes('round_trip') ? '70' : '35'}!
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {currentStep < 7 && !showZellePending && (
        <MobileBookingActionBar
          onClick={handleNext}
          buttonClassName="bg-[#38B6FF] text-white hover:bg-blue-500"
          dataTestId="transport-mobile-next-btn"
        >
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </MobileBookingActionBar>
      )}
      
      {/* Zelle Pending Payment Screen */}
      {showZellePending && zelleBookingData && (
        <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <img src="/zelle-logo.png" alt="Zelle" className="h-12 w-auto" />
              </div>
              <div className="inline-block bg-amber-100 text-amber-800 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                PENDING PAYMENT
              </div>
              <h2 className="text-2xl font-bold text-slate-900 font-['Outfit']">Complete Your Zelle Payment</h2>
              <p className="text-slate-600 mt-2">Your booking is held. Please complete the Zelle transfer to finalize.</p>
            </div>
            
            {/* Zelle Payment Details */}
            <div className="bg-slate-50 rounded-xl p-5 mb-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="text-slate-600 font-medium">Zelle ID</span>
                  <span className="text-slate-900 font-bold">platinumtouchnj@gmail.com</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="text-slate-600 font-medium">Account Name</span>
                  <span className="text-slate-900 font-bold">Platinum Touch NJ</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="text-slate-600 font-medium">Original Price</span>
                  <span className="text-slate-500 line-through">${zelleBookingData.basePrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="text-green-600 font-medium">Zelle Discount</span>
                  <span className="text-green-600 font-bold">-${zelleBookingData.discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-purple-100 rounded-lg px-3 -mx-3">
                  <span className="text-purple-900 font-bold text-lg">Amount to Send</span>
                  <span className="text-purple-600 font-bold text-2xl">${zelleBookingData.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">Payment Instructions:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Open your bank's Zelle feature</li>
                <li>Send <span className="font-bold">${zelleBookingData.total.toFixed(2)}</span> to <span className="font-bold">platinumtouchnj@gmail.com</span></li>
                <li>Include your name "{zelleBookingData.customerName}" in the memo</li>
                <li>Once payment is received, your booking will be confirmed</li>
              </ol>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/')}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
              >
                Done - Return Home
              </Button>
            </div>
            
            <p className="text-center text-xs text-slate-500 mt-4">
              Booking ID: {zelleBookingData.bookingId}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
